import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, CircularProgress, Chip,
  Paper, IconButton, Divider, Alert, LinearProgress,
  Autocomplete, TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Add as AddIcon,
  ArrowBack as BackIcon,
  SettingsAccessibility as PsychoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';

// Estados de error del backend (p. ej. "error_generacion_informe"): se detectan
// por contención para no depender del string exacto.
const isErrorState = (status) => {
  const s = (status || '').toLowerCase();
  return s.includes('error') || s.includes('fallido') || s.includes('failed');
};

// Estado final = completado (éxito) o cualquier estado de error.
// (El backend usa estados intermedios como procesando_gpt, generando_html, etc.)
const isInFinalState = (status) => {
  const s = (status || '').toLowerCase();
  return s === 'completado' || s === 'completed' || isErrorState(s);
};

const FileRow = ({ label, file, required = false, t }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}>
    {file ? (
      <CheckIcon sx={{ color: '#10B981', fontSize: 20, flexShrink: 0 }} />
    ) : (
      <EmptyIcon sx={{ color: required ? '#EF4444' : '#D1D5DB', fontSize: 20, flexShrink: 0 }} />
    )}
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight={600} color={file ? 'text.primary' : 'text.disabled'}>
        {label}
      </Typography>
      {!file && (
        <Typography variant="caption" color={required ? 'error' : 'text.disabled'}>
          {required
            ? t('ats.psycholaboral.notAvailableRequired')
            : t('ats.psycholaboral.notAvailable')
          }
        </Typography>
      )}
    </Box>
    {file && (
      <Chip
        label={t('ats.psycholaboral.available')}
        size="small"
        color="success"
        variant="outlined"
        sx={{ height: 18, fontSize: '0.65rem' }}
      />
    )}
  </Box>
);

const AtsPsycholaboralDialog = ({ open, onClose, position, positionId, applications }) => {
  const { t } = useTranslation();
  const { project, organization, authToken, user, showAlert } = useStore.getState();

  const [view, setView] = useState('list');
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  const [selectedApplication, setSelectedApplication] = useState(null);
  const [files, setFiles] = useState({ cv: null, disc: null, reasoning: null, interview: null });
  const [loadingFiles, setLoadingFiles] = useState(false);

  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replacing, setReplacing] = useState(false);

  const pollingRef = useRef(null);

  const activeApplications = (applications || []).filter((a) => a.status !== 'discarded');

  // Candidatos que ya tienen un informe psicolaboral en esta vacante (por applicant_id):
  // se excluyen del selector para no crear más de uno. Se reemplaza desde la lista.
  const applicantsWithReport = new Set(
    (reports || []).map((r) => r.applicant_id).filter(Boolean)
  );
  const availableApplications = activeApplications.filter(
    (a) => !applicantsWithReport.has(a.applicantId)
  );

  // Reset al abrir/cerrar
  useEffect(() => {
    if (!open) {
      stopPolling();
      setView('list');
      setSelectedApplication(null);
      setFiles({ cv: null, disc: null, reasoning: null, interview: null });
      setGenerating(false);
      setGenerationStatus(null);
      setGeneratedReport(null);
      return;
    }
    loadReports();
  }, [open]);

  // Cargar archivos al seleccionar candidato
  useEffect(() => {
    if (selectedApplication?.applicantId) {
      loadFiles(selectedApplication);
    } else {
      setFiles({ cv: null, disc: null, reasoning: null, interview: null });
    }
  }, [selectedApplication]);

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      const res = await api.get(`/reports`, {
        params: { projectId: project },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data.response?.data || res.data.data || [];
      const filtered = Array.isArray(data)
        ? data.filter((r) => r.position_id === positionId)
        : [];
      setReports(filtered);
    } catch (err) {
      console.error(err);
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  // Reemplazar: elimina el informe existente y abre la creación para regenerar.
  const doReplace = async () => {
    const report = replaceTarget;
    if (!report?.id) { setReplaceTarget(null); return; }
    setReplacing(true);
    // Detener cualquier polling activo antes de borrar el informe.
    stopPolling();
    try {
      await api.delete(`/reports/${report.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      showAlert(t('ats.psycholaboral.replaceSuccess'), 'success');
      setReplaceTarget(null);
      await loadReports();
      // Ir siempre al flujo de creación con el candidato preseleccionado, para que
      // se muestre el checklist de archivos igual que en una evaluación nueva.
      const app = activeApplications.find(
        (a) =>
          (a.uid || a.id) === report.applicationId ||
          a.applicantId === report.applicant_id
      ) || null;
      setSelectedApplication(app);
      setView('create');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.psycholaboral.replaceError'), 'error');
    } finally {
      setReplacing(false);
    }
  };

  const loadFiles = async (application) => {
    if (!application?.applicantId) return;
    setLoadingFiles(true);
    const newFiles = { cv: null, disc: null, reasoning: null, interview: null };

    // CV
    try {
      const res = await api.get(
        `/applicants/${application.applicantId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const applicant = res.data.response?.data || res.data.data;
      if (applicant?.cvUrl) {
        newFiles.cv = {
          name: `CV_${application.candidateName || application.candidateEmail}.pdf`,
          url: applicant.cvUrl,
          stepIndex: 0,
        };
      }
    } catch (err) {
      console.error('Error fetching applicant CV:', err);
    }

    // DISC
    try {
      const res = await api.get(`/disc-tests/by-position`, {
        params: { positionId, projectId: project, applicantId: application.applicantId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data.response?.data || [];
      const evaluated = data.find((d) => d.status === 'evaluated' && d.reportUrl);
      if (evaluated) {
        newFiles.disc = {
          name: `DISC_${application.candidateName || application.candidateEmail}.pdf`,
          url: evaluated.reportUrl,
          stepIndex: 2,
        };
      }
    } catch (err) {
      console.error('Error fetching DISC:', err);
    }

    // Reasoning
    try {
      const res = await api.get(`/reasoning-tests/by-position`, {
        params: { positionId, projectId: project, applicantId: application.applicantId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const data = res.data.response?.data || [];
      const evaluated = data.find((r) => r.status === 'evaluated' && r.reportUrl);
      if (evaluated) {
        newFiles.reasoning = {
          name: `Reasoning_${application.candidateName || application.candidateEmail}.pdf`,
          url: evaluated.reportUrl,
          stepIndex: 3,
        };
      }
    } catch (err) {
      console.error('Error fetching reasoning:', err);
    }

    // Entrevista (solo la de esta vacante, filtrada por la postulación)
    try {
      const applicationId = application.uid || application.id;
      const res = await api.get(
        `/interviews/by-position`,
        {
          params: { positionId },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      const data = res.data.response?.data || res.data.data || [];
      const list = Array.isArray(data)
        ? data.filter((i) => i.applicationId === applicationId)
        : [];
      const completed = list.find((i) => i.status === 'completed' && i.transcriptTxtUrl) || null;
      if (completed) {
        newFiles.interview = {
          name: `Entrevista_${application.candidateName || application.candidateEmail}.txt`,
          url: completed.transcriptTxtUrl,
          stepIndex: 4,
        };
      }
    } catch (err) {
      console.error('Error fetching interviews:', err);
    }

    setFiles(newFiles);
    setLoadingFiles(false);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  // Polling del estado del informe por HTTP (mismo enfoque que /reports en el maker,
  // que sí funciona en este entorno; no dependemos de onSnapshot). Consulta cada 4s
  // hasta que el informe llega a un estado final o tiene report_url.
  const startPolling = (reportId) => {
    if (!reportId) return;
    stopPolling();
    pollingRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/reports/${reportId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const report = data.data;
        if (!report) return;
        setGenerationStatus(report.status || t('ats.psycholaboral.statusProcessing'));
        if (isInFinalState(report.status) || report.report_url) {
          stopPolling();
          setGenerating(false);
          setGeneratedReport({ id: reportId, ...report });
          loadReports();
        }
      } catch (err) {
        console.error('Error en el polling del psicolaboral:', err);
      }
    }, 4000);
  };

  const handleGenerate = async () => {
    if (!selectedApplication || !files.cv) return;
    setGenerating(true);
    setGenerationStatus(t('ats.psycholaboral.statusCreating'));

    try {
      const candidateName = selectedApplication.candidateName || selectedApplication.candidateEmail;
      const candidateEmail = selectedApplication.candidateEmail;

      // 0. Narrativa por defecto del proyecto. El ATS no tiene selector de
      // narrativa; se usa la default para que el informe (y el resumen
      // ejecutivo) respeten el tono/estilo configurado. No es fatal si falla.
      let selectedNarrative = null;
      try {
        const { data: narrData } = await api.get(
          `/projects/gpt-narratives`,
          {
            params: { projectId: project, organizationId: organization },
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
        if (narrData?.response?.status_response === 'OK') {
          const narratives = narrData.response.data?.narratives || [];
          const def = narratives.find((n) => n.isDefault) || narratives[0] || null;
          if (def?.id && def?.content?.trim()) {
            selectedNarrative = {
              id: def.id,
              name: def.name || '',
              description: def.description || '',
              content: def.content.trim(),
            };
          }
        }
      } catch (err) {
        console.error('Error al cargar la narrativa por defecto del proyecto:', err);
      }

      // 1. POST /reports
      const { data: created } = await api.post(
        `/reports`,
        {
          uid: user.uid,
          email: user.email,
          contexts: ['', '', '', '', ''],
          skipped: [false, false, false, false, false],
          status: 'Creando...',
          projectId: project,
          organizationId: organization,
          position_id: positionId,
          position_name: position.name,
          applicant_name: candidateName,
          applicant_email: candidateEmail,
          applicant_id: selectedApplication.applicantId,
          applicationId: selectedApplication.uid || selectedApplication.id, // NUEVO: vincula el informe con la aplicación del ATS y activa el resumen ejecutivo
          language: 'es',
          dynamicSteps: [],
          selectedNarrative, // NUEVO: narrativa por defecto del proyecto (el ATS no tiene selector)
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const reportId = created.data.id;

      // 2. Adjuntar archivos con URL (sin subir)
      const filesToAdd = [files.cv, files.disc, files.reasoning, files.interview].filter(Boolean);

      for (let i = 0; i < filesToAdd.length; i++) {
        const file = filesToAdd[i];
        setGenerationStatus(
          t('ats.psycholaboral.statusAttaching', { current: i + 1, total: filesToAdd.length })
        );
        await api.patch(
          `/reports/${reportId}/add-file`,
          {
            file: {
              name: file.name,
              type: 'application/pdf',
              url: file.url,
              stepIndex: file.stepIndex,
              context: '',
            },
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }

      await api.patch(
        `/reports/${reportId}/update-status`,
        { status: 'Archivos cargados, procesando con IA...' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // 3. Crear tarea GPT
      setGenerationStatus(t('ats.psycholaboral.statusProcessing'));
      const { data: taskData } = await api.post(
        `/gpt-agent/tasks`,
        {
          reportId,
          instructions:
            'Genera un informe detallado siguiendo la estructura proporcionada, incluyendo emojis, formato profesional y evaluación objetiva del candidato.',
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const taskId = taskData.data.id;

      // 4. Procesar tarea GPT
      await api.post(
        `/gpt-agent/tasks/${taskId}/process`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // 5. Polling del estado del informe (HTTP, como /reports)
      startPolling(reportId);
    } catch (err) {
      console.error(err);
      showAlert(t('ats.psycholaboral.generateError'), 'error');
      setGenerating(false);
      setGenerationStatus(null);
    }
  };

  const formatTs = (ts) => {
    if (!ts) return '—';
    if (ts._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('es-CL');
    if (ts.toDate) return ts.toDate().toLocaleDateString('es-CL');
    return '—';
  };

  return (
    <Dialog
      open={open}
      onClose={!generating ? onClose : undefined}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {view === 'create' && !generating && !generatedReport && (
              <IconButton
                size="small"
                onClick={() => { setView('list'); setSelectedApplication(null); loadReports(); }}
              >
                <BackIcon fontSize="small" />
              </IconButton>
            )}
            <PsychoIcon sx={{ color: '#EC4899' }} />
            <Typography variant="h6" fontWeight={700}>
              {t('ats.psycholaboral.title')}
            </Typography>
          </Box>
          {!generating && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
          {position?.name}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>

        {/* ── VISTA LISTA ── */}
        {view === 'list' && (
          <Box>
            {loadingReports ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : reports.length === 0 ? (
              <Alert severity="info">
                {t('ats.psycholaboral.noReports')}
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {reports.map((report) => (
                  <Paper
                    key={report.id}
                    elevation={0}
                    sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {report.applicant_name || report.applicant_email || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTs(report.created_at)}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <Chip
                            label={report.status || '—'}
                            size="small"
                            color={
                              isErrorState(report.status)
                                ? 'error'
                                : isInFinalState(report.status)
                                ? 'success'
                                : 'default'
                            }
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        {report.report_url && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(report.report_url, '_blank')}
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {t('ats.psycholaboral.viewReport')}
                          </Button>
                        )}
                        {report.resumen_ejecutivo_pdf_url && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(report.resumen_ejecutivo_pdf_url, '_blank')}
                            sx={{ fontSize: '0.7rem', borderColor: '#EC4899', color: '#EC4899' }}
                          >
                            {t('ats.psycholaboral.downloadSummary')}
                          </Button>
                        )}
                        <Button
                          size="small"
                          variant="text"
                          color="error"
                          onClick={() => setReplaceTarget(report)}
                          sx={{ fontSize: '0.7rem' }}
                        >
                          {t('ats.psycholaboral.replace')}
                        </Button>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* ── VISTA CREAR ── */}
        {view === 'create' && !generatedReport && (
          <Box>
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#FDF2F8', border: '1px solid #FBCFE8', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('ats.modules.psicolaboralRecommendation')}
              </Typography>
            </Box>
            <Autocomplete
              options={availableApplications}
              getOptionLabel={(opt) =>
                opt.candidateName || opt.candidateEmail || opt.applicantId
              }
              value={selectedApplication}
              onChange={(_, val) => setSelectedApplication(val)}
              disabled={generating}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('ats.psycholaboral.selectCandidate')}
                  size="small"
                />
              )}
              sx={{ mb: 3 }}
            />

            {selectedApplication && (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ display: 'block', mb: 1 }}
                >
                  {t('ats.psycholaboral.filesTitle')}
                </Typography>

                {loadingFiles ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{ border: '1px solid #E5E7EB', borderRadius: 2, px: 2, mb: 2 }}
                  >
                    <FileRow label={t('ats.psycholaboral.fileCV')}        file={files.cv}        required t={t} />
                    <Divider />
                    <FileRow label={t('ats.psycholaboral.fileDISC')}      file={files.disc}      t={t} />
                    <Divider />
                    <FileRow label={t('ats.psycholaboral.fileReasoning')} file={files.reasoning} t={t} />
                    <Divider />
                    <FileRow label={t('ats.psycholaboral.fileInterview')} file={files.interview} t={t} />
                  </Paper>
                )}

                {!files.cv && !loadingFiles && (
                  <Alert severity="error">
                    {t('ats.psycholaboral.noCvAlert')}
                  </Alert>
                )}
              </>
            )}

            {generating && generationStatus && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    '& .MuiLinearProgress-bar': { backgroundColor: '#EC4899' },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {generationStatus}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── VISTA RESULTADO ── */}
        {generatedReport && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {!isErrorState(generatedReport.status) ? (
              <>
                <CheckIcon sx={{ fontSize: 48, color: '#10B981', mb: 1 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {t('ats.psycholaboral.generateSuccess')}
                </Typography>
                {generatedReport.report_url && (
                  <Button
                    variant="contained"
                    onClick={() => window.open(generatedReport.report_url, '_blank')}
                    sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, mt: 1 }}
                  >
                    {t('ats.psycholaboral.viewReport')}
                  </Button>
                )}
              </>
            ) : (
              <Alert severity="error">
                {t('ats.psycholaboral.generateError')}
              </Alert>
            )}
          </Box>
        )}

      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        {view === 'list' && (
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadReports}
              disabled={loadingReports}
              size="small"
              color="inherit"
            >
              {t('ats.psycholaboral.refresh')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setView('create')}
              sx={{ backgroundColor: '#EC4899', '&:hover': { backgroundColor: '#DB2777' } }}
            >
              {t('ats.psycholaboral.newEvaluation')}
            </Button>
          </>
        )}

        {view === 'create' && !generatedReport && (
          <>
            <Button color="inherit" onClick={onClose} disabled={generating}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={!selectedApplication || !files.cv || loadingFiles || generating}
              startIcon={
                generating
                  ? <CircularProgress size={16} color="inherit" />
                  : <PsychoIcon />
              }
              sx={{ backgroundColor: '#EC4899', '&:hover': { backgroundColor: '#DB2777' } }}
            >
              {generating
                ? t('ats.psycholaboral.generating')
                : t('ats.psycholaboral.generate')
              }
            </Button>
          </>
        )}

        {generatedReport && (
          <Button
            variant="contained"
            onClick={() => { setGeneratedReport(null); setView('list'); setSelectedApplication(null); loadReports(); }}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, ml: 'auto' }}
          >
            {t('ats.psycholaboral.backToList')}
          </Button>
        )}
      </DialogActions>

      {/* Confirmación de reemplazo (con aviso de consumo de puntos) */}
      <Dialog
        open={!!replaceTarget}
        onClose={() => !replacing && setReplaceTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('ats.psycholaboral.replaceTitle')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('ats.psycholaboral.replaceWarning')}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            {t('ats.psycholaboral.replaceConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceTarget(null)} disabled={replacing}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={doReplace}
            disabled={replacing}
            startIcon={replacing ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {t('ats.psycholaboral.replace')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AtsPsycholaboralDialog;